import { forwardRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography } from "@mui/material";
import {
  GENERATING_CUSTOM_REPORT_MESSAGE,
  GENERATING_CUSTOM_REPORT_SUCCESS,
  GENERATING_CUSTOM_REPORT_ERROR,
  DISCARD_GENERATING_CUSTOM_REPORT_ALERT_DESCRIPTION,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { ALERTS_ASYNC_STATUS } from "../../utils/constants";
import { CREATIVE_JOB_TYPES } from "../../utils/errors";
import { openAlertModal } from "../../utils/alertModalsState";
import { getCustomReportSourcePath } from "../../utils/customReportSource";
import { openGeneratedCustomReportFile } from "../../utils/customReportFile";
import LinearLoaderWithTime from "../Loaders/LinearLoaderWithTime";
import TaskAlertCard from "./TaskAlertCard";
import TaskAlertErrorMessage from "./TaskAlertErrorMessage";
import TaskAlertDiscardActions from "./TaskAlertDiscardActions";
import { useCreativeJobProgressLoader } from "../../hooks/useCreativeJobProgressLoader";

const getTaskVisualStatus = ({ isError, isSuccess, isLoading }) => {
  if (isError) return "error";
  if (isSuccess) return "success";
  if (isLoading) return "inProgress";
  return "pending";
};

const GeneratingCustomReportAlertItem = forwardRef(
  ({ alert, onClose }, ref) => {
    const { t } = useTranslationApp();
    const navigate = useNavigate();
    const location = useLocation();
    const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
    const { id, status, progress, generationData, message, messageKey, jobID } =
      alert;

    const isError = status === ALERTS_ASYNC_STATUS.ERROR;
    const isSuccess = status === ALERTS_ASYNC_STATUS.SUCCESS;
    const isLoading =
      status === ALERTS_ASYNC_STATUS.LOADING ||
      status === ALERTS_ASYNC_STATUS.GENERATING;
    const isTerminal = isError || isSuccess;
    const storageKey = jobID ?? id;

    const {
      loaderTimeMs,
      onProgressChange,
      seedProgress,
      formattedRemainingTime,
    } = useCreativeJobProgressLoader({
      storageKey,
      migrateFromKey: id,
      jobType: CREATIVE_JOB_TYPES.generateCustomReport,
      isTerminal,
    });

    const getTranslatedMessage = () => {
      if (isError) return t(GENERATING_CUSTOM_REPORT_ERROR);
      if (isSuccess) return t(GENERATING_CUSTOM_REPORT_SUCCESS);
      if (messageKey) return t(messageKey);
      return t(GENERATING_CUSTOM_REPORT_MESSAGE);
    };

    const onAlertClick = () => {
      if (isLoading || isConfirmingDiscard) {
        return;
      }

      if (isSuccess) {
        const downloadUrl =
          generationData?.downloadUrl || generationData?.url || null;
        if (downloadUrl) {
          openGeneratedCustomReportFile({
            downloadUrl,
            fileName: generationData?.fileName,
            format: generationData?.format,
          });
          return;
        }
      }

      const sourcePath =
        generationData?.sourcePath ||
        getCustomReportSourcePath({ campaignID: generationData?.campaignID });
      const currentPath = `${location.pathname}${location.search || ""}`;

      if (sourcePath && currentPath !== sourcePath) {
        navigate(sourcePath);
      }

      openAlertModal(id, generationData, () => {});
    };

    const taskVisualStatus = getTaskVisualStatus({
      isError,
      isSuccess,
      isLoading,
    });
    const displayProgress = progress || (isLoading ? seedProgress : 100);
    const showDiscardButton = isError || isSuccess;

    return (
      <TaskAlertCard
        ref={ref}
        status={taskVisualStatus}
        label={getTranslatedMessage()}
        estimatedTime={isLoading ? formattedRemainingTime : null}
        progress={displayProgress}
        onClick={isLoading ? undefined : onAlertClick}
        cursor={isLoading ? "default" : "pointer"}
        showProgressBar={!isLoading}
        showProgressPercent={!isLoading}
        progressSlot={
          isLoading ? (
            <LinearLoaderWithTime
              isAlert={true}
              labelSide="bottom"
              time={loaderTimeMs}
              progress={seedProgress}
              autoIncrement={true}
              max={99}
              onProgressChange={onProgressChange}
            />
          ) : null
        }
        details={
          message ? (
            isError ? (
              <TaskAlertErrorMessage>
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  {message}
                </Typography>
              </TaskAlertErrorMessage>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.75rem" }}
              >
                {message}
              </Typography>
            )
          ) : null
        }
        actions={
          showDiscardButton ? (
            <TaskAlertDiscardActions
              isConfirming={isConfirmingDiscard}
              descriptionKey={
                DISCARD_GENERATING_CUSTOM_REPORT_ALERT_DESCRIPTION
              }
              onStartDiscard={() => {
                if (isSuccess) {
                  setIsConfirmingDiscard(true);
                  return;
                }
                onClose(id);
              }}
              onConfirm={() => onClose(id)}
              onCancel={() => setIsConfirmingDiscard(false)}
              onView={isSuccess ? onAlertClick : null}
            />
          ) : null
        }
      />
    );
  }
);

GeneratingCustomReportAlertItem.displayName = "GeneratingCustomReportAlertItem";

export default GeneratingCustomReportAlertItem;
